import React, { Dispatch, memo, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormikProvider, getIn } from 'formik';

import { useEventCreationForm } from './useEventCreationForm';
import { ICalendarEvent, IPositionedEvent } from '~/Screens/Calendar/interfaces';
import { useEventCreationFormInputs } from './useEventCreationFormInputs';
import { EventType } from '~/Models/DTO/Event.model';
import { makeStyles } from '~/UIKit/Style';
import { CHARCOAL_GREY, FADED_ICON_ERROR, VERTICAL_SEPARATOR } from '~/Constants/Colors.constants';
import { Button } from '~/UIKit';
import { EventCreationStep } from '~/Screens/Calendar/CalendarView/components/CreatingEvent/types';
import { useShowAdvancedViewClick } from './useShowAdvancedViewClick';
import { useProvider } from '~/Hooks';

export const useStyles = makeStyles(
  () => ({
    container: {
      color: CHARCOAL_GREY,
      width: '100%',
      borderBottom: `1px solid ${VERTICAL_SEPARATOR}`,
      padding: '0 15px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      minHeight: 50,
      position: 'relative',
    },
    hasError: {
      backgroundColor: FADED_ICON_ERROR,
    },
    disabled: {
      opacity: 0.4,
    },
    showAdvancedViewButton: {
      margin: '9px auto',
      display: 'flex',
    },
    buttons: {
      display: 'flex',
      padding: '0 15px 15px 0',
    },
    actionButton: {
      width: '100%',
      marginLeft: 15,
    },
  }),
  { name: 'EventCreationForm' },
);

interface ICreatingEventForm {
  event: ICalendarEvent;
  onCancel: () => void;
  setNewEvent: Dispatch<SetStateAction<null | IPositionedEvent>>;
  setCreationStep: Dispatch<SetStateAction<EventCreationStep>>;
}

export const EventCreationForm = memo(
  ({ event, setNewEvent, onCancel, setCreationStep }: ICreatingEventForm) => {
    const { classes } = useStyles();
    const { t } = useTranslation();
    const { isUsingThePartnerTool } = useProvider();
    const { formik } = useEventCreationForm({ event, setNewEvent, setCreationStep });
    const { onShowAdvancedViewClick } = useShowAdvancedViewClick(formik.values);
    const inputOptions = useEventCreationFormInputs();
    const formInputs = useMemo(() => {
      switch (event.type) {
        case undefined:
          return [];
        case EventType.FREE_TRAINING:
          return [inputOptions.name, inputOptions.location, inputOptions.sportCategory];
        default:
          return [
            inputOptions.name,
            inputOptions.startTime,
            inputOptions.endTime,
            inputOptions.trainer,
            inputOptions.location,
            inputOptions.sportCategory,
            inputOptions.seats,
            ...(isUsingThePartnerTool ? [] : [inputOptions.isPublic]),
          ];
      }
    }, [event.type, inputOptions, isUsingThePartnerTool]);

    return (
      <FormikProvider value={formik}>
        {formInputs.map(({ FormInput, ...props }) => (
          <FormInput
            {...(props as any)}
            key={props.name}
            className={classes.container}
            handleBlur={formik.handleBlur}
            handleChange={formik.handleChange}
            setFieldValue={formik.setFieldValue}
            value={getIn(formik.values, props.name)}
            errors={getIn(formik.errors, props.name)}
            touched={getIn(formik.touched, props.name)}
          />
        ))}
        <Button
          size="small"
          variant="text"
          onClick={onShowAdvancedViewClick}
          className={classes.showAdvancedViewButton}
        >
          {t('app.button.showAdvancedView')}
        </Button>
        <div className={classes.buttons}>
          <Button
            size="small"
            variant="outlined"
            onClick={onCancel}
            className={classes.actionButton}
          >
            {t('app.cancel')}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={formik.handleSubmit as any}
            className={classes.actionButton}
          >
            {t('app.title.create')}
          </Button>
        </div>
      </FormikProvider>
    );
  },
);
